// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CheckboxInput.tsx
 * @input Uses React, useId, ChangeEvent, FieldLabel, FieldStatus, IconType, InputStatus, useTooltip
 * @output Exports CheckboxInput component, CheckboxInputProps
 * @position Core implementation; consumed by index.ts, tested by CheckboxInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/CheckboxInput/CheckboxInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/CheckboxInput/CheckboxInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/CheckboxInput/index.ts (exports if types change)
 * - /apps/storybook/stories/CheckboxInput.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/CheckboxInput/ (showcase blocks)
 */
import { useId, useCallback, use, useOptimistic, useRef, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typographyVars, typeScaleVars, fontWeightVars } from "../theme/tokens.stylex.js";
import { FieldLabel } from "../Field/FieldLabel.js";
import { FieldStatus } from "../FieldStatus/FieldStatus.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { indicatorScope } from "../Indicator/indicator.markers.stylex.js";
import { useIndicatorFocusRing } from "../hooks/useIndicatorFocusRing.js";
import { useIndicator } from "../Indicator/index.js";
import { themeProps } from "../utils/themeProps.js";
import { CheckboxListContext } from "../CheckboxList/CheckboxListContext.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  containerLabelHidden: {
    kOIVth: "xxhr3t",
    $$css: true
  },
  checkboxWrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kHBbk8: "xc8icb0",
    $$css: true
  },
  input: {
    kVAEAm: "x10l6tqk",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kkrTdU: "x1ypdohk",
    kY2c9j: "x1vjfegm",
    k7Eaqz: "xkagaj0",
    kAzted: "x80b3aj",
    k87sOh: "xijlfn0",
    kLqNvP: "x1hsn8va",
    k3aq6I: "x8i4i9p",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const wrapperSizeStyles = {
  sm: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
// Dynamic field width (number -> px, string used as-is).
const dynamicWidthStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};

/**
 * A checkbox input component for toggling boolean values.
 *
 * @example
 * ```
 * <CheckboxInput
 *   label="Accept terms"
 *   value={accepted}
 *   onChange={setAccepted}
 * />
 * <CheckboxInput
 *   label="Subscribe"
 *   description="Receive weekly updates"
 *   value={subscribed}
 *   onChange={setSubscribed}
 * />
 * ```
 */
export function CheckboxInput({
  label,
  isLabelHidden = false,
  description,
  onChange,
  changeAction,
  isLoading = false,
  value,
  isDisabled = false,
  htmlName,
  disabledMessage,
  isReadOnly = false,
  isOptional = false,
  isRequired = false,
  size = 'md',
  onFocus,
  onBlur,
  labelIcon,
  status,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the checkbox row (which already exists) and the
  // native checkbox stays perceivable via aria-disabled instead of the disabled
  // attribute. Value mutation is blocked by the isDisabled guard in onChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  // Keep the native checkbox focusable via aria-disabled either when it renders
  // its own reason tooltip, or when it sits in a CheckboxList whose whole-group
  // `disabledMessage` (shown on the group container) needs each checkbox to
  // stay keyboard-perceivable. The group signals this through context rather
  // than a public prop.
  const checkboxListContext = use(CheckboxListContext);
  const isFocusableDisabled = isDisabled && (showsDisabledMessage || (checkboxListContext?.hasDisabledMessage ?? false));
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container row is not naturally focusable; focusin bubbles up from the
    // native checkbox, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // The checkbox visual is an indicator: a theme can restyle it through the
  // `checkbox` target or replace the component outright.
  const CheckboxControl = useIndicator('checkbox');
  // The ring is drawn on the indicator itself: the native input is
  // `opacity: 0`, and only the indicator's own element can shape the outline
  // to match it. See useIndicatorFocusRing.
  const indicatorRef = useRef(null);
  const {
    focusProps
  } = useIndicatorFocusRing(indicatorRef, isDisabled);
  const isIndeterminate = optimisticValue === 'indeterminate';
  const isChecked = optimisticValue === true;

  // Sync the native indeterminate DOM property (can't be set via JSX
  // attribute). On a native checkbox this is the authoritative way to expose
  // the mixed state — a separate aria-checked="mixed" would be redundant and
  // can desync from / override the native state (forms-16), so it is omitted.
  const indeterminateRef = useCallback(el => {
    if (el) {
      el.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Build aria-describedby from description and status message
  // Only include descriptionID when the element actually renders.
  // FieldLabel renders the description (with descriptionID) even when the
  // label is visually hidden — it's sr-only, so keep it linked.
  const describedByParts = [];
  if (description) {
    describedByParts.push(descriptionID);
  }
  if (status?.message) {
    describedByParts.push(statusMessageID);
  }
  if (showsDisabledMessage) {
    describedByParts.push(disabledMessageTooltip.describedBy);
  }
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
  return /*#__PURE__*/_jsxs("div", {
    ...mergeProps(themeProps('checkbox-input', {
      size
    }), stylex.props(width != null && dynamicWidthStyles.width(width), xstyle), className, style),
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        // Interaction (hover/focus) listeners for the disabled-message
        // tooltip attach to the whole row for a larger trigger target;
        // positioning anchors on the checkbox itself (below) so the tooltip
        // appears next to the control, not the far edge of the row.
        // Handlers are gated internally by isEnabled, so attaching
        // unconditionally is safe.
        disabledMessageTooltip.interactionRef(el);
      },
      ...stylex.props(styles.container, isLabelHidden && styles.containerLabelHidden,
      // Hover and focus reach the checkbox visual through this ancestor
      // marker rather than props, so the whole row drives it.
      !isDisabled && indicatorScope),
      children: [/*#__PURE__*/_jsxs("div", {
        ...stylex.props(styles.checkboxWrapper, wrapperSizeStyles[size]),
        ...focusProps,
        children: [/*#__PURE__*/_jsx("input", {
          ...rest,
          ref: mergeRefs(ref, indeterminateRef, disabledMessageTooltip.positionRef),
          id: id,
          type: "checkbox"
          // Withhold the name while disabled: with a disabledMessage the
          // input stays focusable (not natively disabled), and a disabled
          // control must not submit.
          ,
          name: isDisabled ? undefined : htmlName,
          checked: isChecked
          // With a disabledMessage the checkbox keeps focusability via
          // aria-disabled so the reason is focus-discoverable; toggling is
          // still blocked by the isDisabled guard in onChange below.
          ,
          disabled: isDisabled && !isFocusableDisabled,
          "aria-disabled": isFocusableDisabled ? 'true' : undefined,
          form: isFocusableDisabled ? '' : undefined,
          readOnly: isReadOnly,
          required: isRequired,
          onChange: e => {
            if (isDisabled || isBusy || isReadOnly) {
              return;
            }
            const checked = e.target.checked;
            onChange?.(checked, e);
            if (changeAction && !e.defaultPrevented) {
              startTransition(async () => {
                setOptimisticValue(checked);
                await changeAction(checked, e);
              });
            }
          },
          onFocus: onFocus,
          onBlur: onBlur,
          "aria-readonly": isReadOnly || undefined,
          "aria-describedby": ariaDescribedBy,
          "aria-invalid": status?.type === 'error' ? true : undefined,
          "aria-busy": isBusy || undefined,
          ...stylex.props(styles.input, wrapperSizeStyles[size], isDisabled && styles.inputDisabled)
        }), /*#__PURE__*/_jsx("span", {
          ref: indicatorRef,
          ...{
            className: "xjp7ctv"
          },
          children: /*#__PURE__*/_jsx(CheckboxControl, {
            state: isIndeterminate ? 'indeterminate' : isChecked ? 'checked' : 'unchecked',
            size: size,
            isDisabled: isDisabled,
            children: isBusy ? /*#__PURE__*/_jsx(Spinner, {
              size: "sm",
              shade: "inherit"
            }) : null
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        ...{
          className: "x78zum5 xdt5ytf x1lsbc85"
        },
        children: /*#__PURE__*/_jsx(FieldLabel, {
          label: label,
          inputID: id,
          isLabelHidden: isLabelHidden,
          isDisabled: isDisabled,
          isOptional: isOptional,
          isRequired: isRequired,
          labelIcon: labelIcon,
          description: description,
          descriptionID: descriptionID
        })
      })]
    }), status?.message && /*#__PURE__*/_jsx(FieldStatus, {
      type: status.type,
      message: status.message,
      id: statusMessageID,
      variant: "detached"
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
CheckboxInput.displayName = 'CheckboxInput';